import React from 'react';
import {Link} from 'react-router-dom';

import AdminLayout from 'Pages/Admin/AdminLayout';
import {Input, DatePicker, Toggle, Label, WYSIWYG, FormHandler, Select} from 'Components/Form';
import {Loading, Alert} from 'Components/Partials';
import {PrimaryButton, SecondaryButton} from 'Components/Button';

import Sessions from 'Services/Api/Admin/MainStage/Sessions';
import GroupsApi from 'Services/Api/Groups/Groups';

import {Toast, Modal} from 'Services';

import CheckOverlapModal from './CheckOverlapModal';

class Store extends React.Component {
    /**
     * @var success
     * @type {string}
     */
    success = 'Session has been submitted successfully';

    /**
     * @var state
     * @type {{session_id, loading: boolean}}
     */
    state = {
        loading: false,
        session_id: this.props.match?.params?.session ?? '',
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        if (this.state.session_id) {
            this.fetchSession();
        } else {
            this.props.setForm({
                stream_src: 'primary',
                supported_languages: Object.keys(window.base.languages),
            });
        }
    };

    /**
     * @method fetchSession
     * @return {Promise<void>}
     */
    fetchSession = async () => {
        this.setState({
            loading: true
        });

        const request = await Sessions.get({
            session: this.state.session_id
        });

        if (request.success) {
            this.props.setInitialValues(request.data.data);

            return this.setState({
                loading: false
            });
        }

        return Toast.error();
    };

    handleSubmit = async () => {
        const {form, handleSubmit} = this.props;
        const {session_id} = this.state;

        if (!form.starts_at) {
            return;
        }

        const request = await Sessions.checkSessionOverlap({
            starts_at: form.starts_at,
            ends_at: form.ends_at,
            supported_languages: form.supported_languages,
            id_to_ignore: session_id
        });

        if (request.overlap) {
            Modal.open({
                component: CheckOverlapModal,
                props: {
                    form,
                    callbackOnSubmit: () => handleSubmit(null, this.handleSubmit2, this.success, !session_id)
                }
            })
        } else {
            handleSubmit(null, this.handleSubmit2, this.success, !session_id);
        }
    }

    /**
     * @method handleSubmit2
     * @param {object} form
     * @return {Promise<boolean|{status: number}>|Promise<AxiosResponse<any>>|*}
     */
    handleSubmit2 = (form) => {
        if (this.state.session_id) {
            return Sessions.patch({
                session_id: this.state.session_id
            }, form);
        } else {
            return Sessions.post(null, form);
        }
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {handleInput, getFieldError, handleSubmit, alert, working, form} = this.props;
        const {loading, session_id} = this.state;

        let languages = Object.entries(window.base.languages).map((value, key) => {
            return {
                label: value[1],
                value: value[0]
            };
        });

        return (
            <AdminLayout title={'Main Stage - ' + (session_id ? 'Update' : 'Create') + ' Session'}>
                {loading && (
                    <div className="p-6">
                        <Loading />
                    </div>
                )}

                {!loading && (
                    <form className="divide-y divide-gray-200">
                        <div className="mx-6">
                            {alert && (<Alert {...alert} />)}

                            <Input
                                containerClassName="mb-4"
                                label="Title"
                                value={form.title}
                                error={getFieldError('title')}
                                onChange={(v) => handleInput('title', v)}
                            />

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <DatePicker
                                        label="Starts At"
                                        selected={form.starts_at}
                                        error={getFieldError('starts_at')}
                                        onChange={date => handleInput('starts_at', date)}
                                        showTimeSelect={true}
                                        timeIntervals={5}
                                        nowButton={true}
                                    />
                                </div>

                                <div>
                                    <DatePicker
                                        label="Ends At"
                                        selected={form.ends_at}
                                        error={getFieldError('ends_at')}
                                        onChange={date => handleInput('ends_at', date)}
                                        showTimeSelect={true}
                                        minutes={5}
                                        timeIntervals={5}
                                        nowButton={true}
                                    />
                                </div>
                            </div>

                            {languages.length > 1 &&
                                <div>
                                    <Select
                                        containerClassName="mb-4"
                                        label="Supported Languages"
                                        value={form.supported_languages}
                                        error={getFieldError('supported_languages')}
                                        onChange={(v) => handleInput('supported_languages', v)}
                                        options={languages}
                                        isMulti
                                    />
                                </div>
                            }

                            <WYSIWYG
                                containerClassName="mb-4"
                                label="Description"
                                value={form.description}
                                error={getFieldError('description')}
                                onChange={(v) => handleInput('description', v)}
                            />

                            <div className="mb-4">
                                <div className="flex justify-between">
                                    <Label label="Primary Stream src (e.g https://player.vimeo.com/video/76979871)" />

                                    <Toggle
                                        value={form.prerecorded}
                                        error={getFieldError('prerecorded')}
                                        onChange={(v) => handleInput('prerecorded', v)}
                                        label="Prerecorded"
                                    />
                                </div>

                                <Input
                                    value={form.primary_stream_src}
                                    error={getFieldError('primary_stream_src')}
                                    onChange={(v) => handleInput('primary_stream_src', v)}
                                />
                            </div>

                            <div className="mb-4">
                                <Input
                                    label="Secondary Stream src (e.g https://player.vimeo.com/video/76979871)"
                                    value={form.secondary_stream_src}
                                    error={getFieldError('secondary_stream_src')}
                                    onChange={(v) => handleInput('secondary_stream_src', v)}
                                />
                            </div>

                            <div className="mb-4">
                                <Input
                                    label="Subtitle Primary Stream src"
                                    value={form.subtitle_primary_stream_src}
                                    error={getFieldError('subtitle_primary_stream_src')}
                                    onChange={(v) => handleInput('subtitle_primary_stream_src', v)}
                                />
                            </div>

                            <div className="mb-4">
                                <Input
                                    label="Subtitle Secondary Stream src"
                                    value={form.subtitle_secondary_stream_src}
                                    error={getFieldError('subtitle_secondary_stream_src')}
                                    onChange={(v) => handleInput('subtitle_secondary_stream_src', v)}
                                />
                            </div>

                            <div className="mb-4">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">Translations (Clevercast)</h3>
                                <div className="grid grid-cols-4 gap-4 mb-4">
                                    <Toggle
                                        value={form.clevercast_enabled}
                                        error={getFieldError('clevercast_enabled')}
                                        onChange={(v) => handleInput('clevercast_enabled', v)}
                                        label="Enable Clevercast"
                                    />
                                </div>

                                <Input
                                    label="Clevercast Primary embed URL"
                                    value={form.clevercast_primary_src}
                                    error={getFieldError('clevercast_primary_src')}
                                    onChange={(v) => handleInput('clevercast_primary_src', v)}
                                />
                            </div>

                            <div className="mb-4">
                                <Input
                                    label="Clevercast Backup embed URL (optional)"
                                    value={form.clevercast_backup_src}
                                    error={getFieldError('clevercast_backup_src')}
                                    onChange={(v) => handleInput('clevercast_backup_src', v)}
                                />
                            </div>

                            <div className="mb-4">
                                <Select
                                    label="Stream src"
                                    value={form.stream_src}
                                    error={getFieldError('stream_src')}
                                    onChange={(v) => handleInput('stream_src', v)}
                                    options={[
                                        { value: 'primary', label: 'Primary', },
                                        { value: 'secondary', label: 'Secondary', },
                                    ]}
                                />
                            </div>

                            <div className="mb-4">
                                <Input
                                    label="Outage Message"
                                    value={form.outage_message}
                                    error={getFieldError('outage_message')}
                                    onChange={(v) => handleInput('outage_message', v)}
                                />
                            </div>

                            {window.base.features.groups &&
                                <Select
                                    containerClassName="mb-4"
                                    label="Groups"
                                    value={form.group_ids}
                                    error={getFieldError('group_ids')}
                                    onChange={(v) => handleInput('group_ids', v)}
                                    isAsync
                                    isMulti
                                    searchCallback={(data) => GroupsApi.get(null, {
                                        ...data,
                                        hasNoTeam: true
                                    })}
                                    searchLabelKey="name"
                                    instructions="Leave empty to open the stream to all users"
                                />
                            }

                            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                Features
                            </h3>

                            <div className="grid grid-cols-4 gap-4 mb-4">
                                <Toggle
                                    value={form.chat}
                                    error={getFieldError('chat')}
                                    onChange={(v) => handleInput('chat', v)}
                                    label="Chat"
                                />

                                <Toggle
                                    value={form.reactions}
                                    error={getFieldError('reactions')}
                                    onChange={(v) => handleInput('reactions', v)}
                                    label="Reactions"
                                />

                                <Toggle
                                    value={form.remote_control}
                                    error={getFieldError('remote_control')}
                                    onChange={(v) => handleInput('remote_control', v)}
                                    label="Remote Control"
                                />

                                <Toggle
                                    value={form.questions}
                                    error={getFieldError('questions')}
                                    onChange={(v) => handleInput('questions', v)}
                                    label="Q&A"
                                />

                                <Toggle
                                    value={form.polls_enabled}
                                    error={getFieldError('polls_enabled')}
                                    onChange={(v) => handleInput('polls_enabled', v)}
                                    label="Polls"
                                />

                                <Toggle
                                    value={form.user_settings}
                                    error={getFieldError('user_settings')}
                                    onChange={(v) => handleInput('user_settings', v)}
                                    label="User Settings"
                                />
                            </div>
                        </div>

                        <div className="px-6 pt-6 flex justify-end gap-4">
                            <Link to="/admin/main-stage">
                                <SecondaryButton type="button" text="Back" />
                            </Link>

                            <PrimaryButton
                                text="Save"
                                working={working}
                                onClick={this.handleSubmit}
                            />
                        </div>
                    </form>
                )}
            </AdminLayout>
        );
    }
}

export default FormHandler(Store);
